import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canStaffSection } from '../utils/staffAccess';
import { useTenantSafe } from '../context/TenantContext';
import { getQuestionnaires, deleteQuestionnaire } from '../services/questionnaireService';
import { getProjects } from '../services/projectService';
import { submitResponse, getResponses, getMyResponses } from '../services/responseService';
import FieldRenderer from '../components/consultation/FieldRenderer';
import QuestionnaireEditor from '../components/consultation/QuestionnaireEditor';
import { useUrlParams } from '../hooks/useUrlParams';
import { DashTile, DashSection, DB_STYLE, downloadFile, FilterChipBar } from '../components/dashboard/DashKit';
import CommunityProjects from '../components/consultation/CommunityProjects';

// ─── RespondentForm ───────────────────────────────────────────────────────────
// Inline response form rendered when a member selects a questionnaire.
const RespondentForm = ({ questionnaire, tenantId, onSubmitted, onBack }) => {
  const [answers, setAnswers] = useState(() => {
    const init = {};
    questionnaire.fields.forEach((f) => { init[f.fieldKey] = f.type === 'checkbox' ? [] : ''; });
    return init;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const formattedAnswers = Object.entries(answers).map(([fieldKey, value]) => ({ fieldKey, value }));
      await submitResponse({
        tenantId,
        projectId: questionnaire.projectId,
        questionnaireId: questionnaire._id,
        answers: formattedAnswers,
        responseScope: 'community_only',
      });
      onSubmitted(questionnaire._id);
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="respondent-form">
      <button type="button" className="btn-secondary btn-sm back-btn" onClick={onBack}>
        ← Back to consultations
      </button>
      <h3>{questionnaire.title}</h3>
      {questionnaire.description && <p className="q-description">{questionnaire.description}</p>}
      {error && <div className="error-msg">{error}</div>}
      <form onSubmit={handleSubmit}>
        {[...questionnaire.fields]
          .sort((a, b) => a.order - b.order)
          .map((field) => (
            <div key={field.fieldKey} className="form-field">
              <label>
                {field.label}
                {field.isRequired && <span className="required"> *</span>}
              </label>
              <FieldRenderer
                field={field}
                value={answers[field.fieldKey]}
                onChange={(key, val) => setAnswers((prev) => ({ ...prev, [key]: val }))}
              />
            </div>
          ))}
        <button type="submit" disabled={submitting}>
          {submitting ? 'Submitting…' : 'Submit Response'}
        </button>
      </form>
    </div>
  );
};

// ─── MemberView ───────────────────────────────────────────────────────────────
// Shown to members and community_staff. Browse open consultations + own history.
const MemberView = ({ tenantId, activeTenant }) => {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [myResponses, setMyResponses]       = useState([]);
  const [loading, setLoading]               = useState(true);
  const [activeTab, setActiveTab]           = useState('open');
  const [selectedQ, setSelectedQ]           = useState(null);
  const [answered, setAnswered]             = useState(new Set());

  useEffect(() => {
    Promise.all([
      getQuestionnaires({ tenantId }),             // active only (backend default)
      getMyResponses({ tenantId }),
    ])
      .then(([qRes, myRes]) => {
        setQuestionnaires(qRes.data);
        setMyResponses(myRes.data);
        setAnswered(new Set(myRes.data.map((r) => String(r.questionnaireId?._id || r.questionnaireId))));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenantId]);

  const handleSubmitted = (qId) => {
    setAnswered((prev) => new Set([...prev, String(qId)]));
    setSelectedQ(null);
    getMyResponses({ tenantId }).then((r) => setMyResponses(r.data)).catch(() => {});
  };

  if (loading) return <p className="page-loading">Loading…</p>;

  if (selectedQ) {
    return (
      <RespondentForm
        questionnaire={selectedQ}
        tenantId={tenantId}
        onSubmitted={handleSubmitted}
        onBack={() => setSelectedQ(null)}
      />
    );
  }

  const communityName = activeTenant?.branding?.displayName || activeTenant?.name;

  return (
    <div className="consultation-member">
      <h3>Community Consultation — {communityName}</h3>

      <div className="tabs">
        <button
          className={activeTab === 'open' ? 'active' : ''}
          onClick={() => setActiveTab('open')}
        >
          Open ({questionnaires.length})
        </button>
        <button
          className={activeTab === 'history' ? 'active' : ''}
          onClick={() => setActiveTab('history')}
        >
          My Responses ({myResponses.length})
        </button>
      </div>

      {activeTab === 'open' && (
        questionnaires.length === 0 ? (
          <p>No active consultations at the moment. Check back soon.</p>
        ) : (
          <ul className="questionnaire-list">
            {questionnaires.map((q) => {
              const alreadyAnswered = answered.has(String(q._id));
              return (
                <li key={q._id} className="questionnaire-card">
                  <div className="questionnaire-card__body">
                    <h4>{q.title}</h4>
                    {q.description && <p>{q.description}</p>}
                    <p className="questionnaire-card__meta">
                      {q.fields.length} question{q.fields.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <button
                    className={alreadyAnswered ? 'btn-secondary' : ''}
                    onClick={() => !alreadyAnswered && setSelectedQ(q)}
                    disabled={alreadyAnswered}
                  >
                    {alreadyAnswered ? '✓ Responded' : 'Respond'}
                  </button>
                </li>
              );
            })}
          </ul>
        )
      )}

      {activeTab === 'history' && (
        myResponses.length === 0 ? (
          <p>You haven't submitted any responses yet.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr><th>Questionnaire</th><th>Submitted</th><th>Answers</th></tr>
            </thead>
            <tbody>
              {myResponses.map((r) => (
                <tr key={r._id}>
                  <td>{r.questionnaireId?.title || '—'}</td>
                  <td>{new Date(r.submittedAt).toLocaleDateString()}</td>
                  <td>{r.answers.length} answer{r.answers.length !== 1 ? 's' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
};

// ─── ManagerView ──────────────────────────────────────────────────────────────
// Shown to community_admin. Create/edit/archive questionnaires + view responses.
const ManagerView = ({ tenantId, activeTenant }) => {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [projects, setProjects]             = useState([]);
  const [loading, setLoading]               = useState(true);
  const [editing, setEditing]               = useState(null);  // null | 'new' | questionnaire
  const [selectedQ, setSelectedQ]           = useState(null);  // questionnaire for response panel
  const [responses, setResponses]           = useState([]);
  const [loadingResp, setLoadingResp]       = useState(false);
  const [msg, setMsg]                       = useState('');
  // Dashboard state
  const [urlP, setUrlP] = useUrlParams({ view: 'list', dateFrom: '', dateTo: '' });
  const view        = urlP.view === 'dashboard' ? 'dashboard' : 'list';
  const dashDateFrom = urlP.dateFrom;
  const dashDateTo   = urlP.dateTo;
  const setView     = (v) => setUrlP('view',    v);
  const setDashFrom = (v) => setUrlP('dateFrom', v);
  const setDashTo   = (v) => setUrlP('dateTo',   v);
  const [dashResponses, setDashResponses]   = useState(null);    // null = not loaded yet
  const [dashLoading, setDashLoading]       = useState(false);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      getQuestionnaires({ tenantId, includeArchived: 'true' }),
      getProjects(tenantId),
    ])
      .then(([qRes, pRes]) => {
        setQuestionnaires(qRes.data);
        setProjects(pRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Lazy-load all responses when switching to dashboard
  useEffect(() => {
    if (view !== 'dashboard' || dashResponses !== null) return;
    setDashLoading(true);
    getResponses({ tenantId })
      .then(res => setDashResponses(res.data))
      .catch(() => setDashResponses([]))
      .finally(() => setDashLoading(false));
  }, [view, dashResponses, tenantId]);

  const handleSaved = (q) => {
    setQuestionnaires((prev) => {
      const idx = prev.findIndex((x) => x._id === q._id);
      if (idx === -1) return [q, ...prev];
      const next = [...prev];
      next[idx] = q;
      return next;
    });
    setEditing(null);
  };

  const handleArchive = async (id) => {
    setMsg('');
    try {
      await deleteQuestionnaire(id);
      setQuestionnaires((prev) =>
        prev.map((q) => (q._id === id ? { ...q, isActive: false } : q))
      );
      if (selectedQ?._id === id) setSelectedQ(null);
    } catch (err) {
      setMsg(err.response?.data?.message || 'Archive failed');
    }
  };

  const handleViewResponses = async (q) => {
    if (selectedQ?._id === q._id) { setSelectedQ(null); return; }
    setSelectedQ(q);
    setLoadingResp(true);
    try {
      const res = await getResponses({ tenantId, questionnaireId: q._id });
      setResponses(res.data);
    } catch {
      setResponses([]);
    } finally {
      setLoadingResp(false);
    }
  };

  if (editing) {
    return (
      <QuestionnaireEditor
        initial={editing === 'new' ? null : editing}
        tenantId={tenantId}
        projects={projects}
        onSave={handleSaved}
        onCancel={() => setEditing(null)}
      />
    );
  }

  if (loading) return <p className="page-loading">Loading…</p>;

  const communityName = activeTenant?.branding?.displayName || activeTenant?.name;

  // ── Dashboard rendering ────────────────────────────────────────────────────
  if (view === 'dashboard') {
    const allR = dashResponses || [];
    let dash = [...allR];
    if (dashDateFrom) {
      const from = new Date(dashDateFrom);
      dash = dash.filter(r => new Date(r.submittedAt) >= from);
    }
    if (dashDateTo) {
      const to = new Date(dashDateTo);
      to.setHours(23, 59, 59, 999);
      dash = dash.filter(r => new Date(r.submittedAt) <= to);
    }
    const hasFilter = !!(dashDateFrom || dashDateTo);

    const totalQ    = questionnaires.length;
    const activeQ   = questionnaires.filter(q => q.isActive).length;
    const archivedQ = totalQ - activeQ;
    const totalR    = dash.length;

    // Responses per questionnaire (filtered)
    const perQ = questionnaires.map(q => ({
      id: q._id,
      title: q.title,
      active: q.isActive,
      count: dash.filter(r => {
        const qid = r.questionnaireId?._id || r.questionnaireId;
        return String(qid) === String(q._id);
      }).length,
    })).sort((a, b) => b.count - a.count);

    const topQ = perQ.filter(q => q.count > 0);

    // Participation rate (only active questionnaires)
    const avgResp = activeQ > 0 ? Math.round(totalR / activeQ) : 0;

    const buildConsultMD = () => {
      const now = new Date();
      const lines = [
        `# Consultation Dashboard — Staff Summary`, '',
        `**Community:** ${communityName}`,
        `**Generated:** ${now.toLocaleDateString('en-GB')} ${now.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}`,
        `**Date filter:** ${hasFilter ? `${dashDateFrom||'start'} → ${dashDateTo||'now'}` : 'All dates'}`,
        '', '---', '',
        '## Questionnaires', '',
        '| Metric | Count |', '|--------|-------|',
        `| Total | ${totalQ} |`,
        `| Active | ${activeQ} |`,
        `| Archived | ${archivedQ} |`,
        '', '---', '',
        '## Responses', '',
        `**Total responses (in view):** ${totalR}`,
        `**Avg responses per active questionnaire:** ${avgResp}`,
        '', '| Questionnaire | Status | Responses |', '|---------------|--------|-----------|',
        ...perQ.map(q => `| ${q.title} | ${q.active ? 'Active' : 'Archived'} | ${q.count} |`),
        '', '---', '',
        '_CIP consultation staff export._',
      ];
      return lines.join('\n');
    };

    const buildConsultCSV = () => {
      const esc = v => `"${String(v??'').replace(/"/g,'""')}"`;
      const hdr = ['Questionnaire','Submitted At','Scope','Member Status'];
      const rows = dash.map(r => {
        const qTitle = questionnaires.find(q => String(q._id) === String(r.questionnaireId?._id || r.questionnaireId))?.title || '';
        return [
          esc(qTitle),
          esc(r.submittedAt ? new Date(r.submittedAt).toLocaleDateString('en-GB') : ''),
          esc(r.responseScope || ''),
          esc(r.membershipStatusAtSubmission || ''),
        ].join(',');
      });
      return [hdr.join(','), ...rows].join('\r\n');
    };

    return (
      <div className="consultation-manager">
        <div className="section-header">
          <h3>Consultations — {communityName}</h3>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn-secondary" onClick={() => setView('list')}>☰ List</button>
            <button onClick={() => setEditing('new')} disabled={projects.length === 0}>
              + New Questionnaire
            </button>
          </div>
        </div>
        {msg && <p className="error-msg">{msg}</p>}

        <div style={{ fontSize:13, color:'#e2e8f0' }}>
          {/* Toolbar */}
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:14 }}>
            <span style={{ fontSize:10, color:'#475569', fontWeight:700, letterSpacing:1 }}>FROM</span>
            <input type="date" value={dashDateFrom} onChange={e => setDashFrom(e.target.value)}
              style={DB_STYLE.dateInput} />
            <span style={{ fontSize:10, color:'#475569', fontWeight:700, letterSpacing:1 }}>TO</span>
            <input type="date" value={dashDateTo} onChange={e => setDashTo(e.target.value)}
              style={DB_STYLE.dateInput} />
            <div style={{ flex:1 }} />
            <button onClick={() => downloadFile(buildConsultMD(), 'consultation-summary.md', 'text/markdown;charset=utf-8')}
              style={DB_STYLE.btnMd}>
              📥 Export .md
            </button>
            <button onClick={() => downloadFile(buildConsultCSV(), 'consultation-responses.csv', 'text/csv;charset=utf-8')}
              style={DB_STYLE.btnCsv}>
              📊 Export .csv
            </button>
          </div>

          {/* Date filter chips */}
          {hasFilter && (
            <FilterChipBar
              chips={[
                dashDateFrom && { label: `From: ${dashDateFrom}`, color: '#0ea5e9', onRemove: () => setDashFrom('') },
                dashDateTo   && { label: `To: ${dashDateTo}`,     color: '#0ea5e9', onRemove: () => setDashTo('') },
              ].filter(Boolean)}
              onClearAll={dashDateFrom && dashDateTo ? () => { setDashFrom(''); setDashTo(''); } : undefined}
            />
          )}

          {dashLoading ? (
            <p style={{ color:'#64748b' }}>Loading responses…</p>
          ) : (
            <>
              {/* Stat tiles */}
              <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:4 }}>
                <DashTile label="Questionnaires" value={totalQ} color="#64748b" />
                <DashTile label="Active" value={activeQ} color="#22c55e" />
                <DashTile label="Archived" value={archivedQ} color="#475569" />
                <DashTile label={`Responses${hasFilter ? ' (filtered)' : ''}`} value={totalR} color="#0ea5e9" />
                <DashTile label="Avg / Active Q" value={avgResp} color="#a855f7" />
              </div>

              {/* Per-questionnaire breakdown */}
              <DashSection label="RESPONSES BY QUESTIONNAIRE" />
              {topQ.length === 0 ? (
                <div style={{ fontSize:12, color:'#334155', fontStyle:'italic' }}>
                  {hasFilter ? 'No responses in selected date range.' : 'No responses yet.'}
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                  {topQ.map(q => {
                    const pct = totalR > 0 ? (q.count / totalR) * 100 : 0;
                    return (
                      <div key={q.id} style={{ background:'#0f172a', borderRadius:6, padding:'8px 12px',
                        borderLeft:`3px solid ${q.active ? '#22c55e' : '#475569'}` }}>
                        <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:4 }}>
                          <span style={{ fontSize:12, color:'#cbd5e1', flex:1 }}>{q.title}</span>
                          <span style={{ fontSize:10, color: q.active ? '#22c55e' : '#475569' }}>
                            {q.active ? 'Active' : 'Archived'}
                          </span>
                          <span style={{ fontSize:12, fontWeight:700, color:'#0ea5e9' }}>{q.count}</span>
                        </div>
                        <div style={{ height:4, borderRadius:3, background:'#1e293b', overflow:'hidden' }}>
                          <div style={{ width:`${pct}%`, height:'100%', background:'#0ea5e9', borderRadius:3 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Questionnaires with zero responses */}
              {(() => {
                const zeroQ = perQ.filter(q => q.count === 0 && q.active);
                if (!zeroQ.length) return null;
                return (
                  <>
                    <DashSection label={`ACTIVE — NO RESPONSES (${zeroQ.length})`} />
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                      {zeroQ.map(q => (
                        <div key={q.id} style={{ background:'#0f172a', borderRadius:5, padding:'5px 10px',
                          border:'1px solid #334155', fontSize:11, color:'#475569' }}>
                          {q.title}
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="consultation-manager">
      <div className="section-header">
        <h3>Consultations — {communityName}</h3>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn-secondary" onClick={() => setView('dashboard')}>📋 Dashboard</button>
          <button onClick={() => setEditing('new')} disabled={projects.length === 0}>
            + New Questionnaire
          </button>
        </div>
      </div>

      {projects.length === 0 && (
        <p className="info-msg">
          Create a project first (in Community Admin → Projects) before adding questionnaires.
        </p>
      )}

      {msg && <p className="error-msg">{msg}</p>}

      {questionnaires.length === 0 ? (
        <p>No questionnaires yet.</p>
      ) : (
        <>
          <table className="admin-table">
            <thead>
              <tr><th>Title</th><th>Status</th><th>Questions</th><th>Created</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {questionnaires.map((q) => (
                <tr key={q._id} className={selectedQ?._id === q._id ? 'row-selected' : ''}>
                  <td>{q.title}</td>
                  <td>
                    <span className={`status-badge status-${q.isActive ? 'active' : 'inactive'}`}>
                      {q.isActive ? 'Active' : 'Archived'}
                    </span>
                  </td>
                  <td>{q.fields.length}</td>
                  <td>{new Date(q.createdAt).toLocaleDateString()}</td>
                  <td className="claim-actions">
                    <button className="btn-secondary btn-sm" onClick={() => setEditing(q)}>Edit</button>
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => handleViewResponses(q)}
                    >
                      {selectedQ?._id === q._id ? 'Hide' : 'Responses'}
                    </button>
                    {q.isActive && (
                      <button
                        className="btn-danger btn-sm"
                        onClick={() => handleArchive(q._id)}
                      >
                        Archive
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {selectedQ && (
            <div className="response-panel">
              <h4>Responses — {selectedQ.title}</h4>
              {loadingResp ? (
                <p>Loading responses…</p>
              ) : responses.length === 0 ? (
                <p>No responses yet.</p>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Submitted</th><th>Scope</th><th>Member status</th><th>Answers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {responses.map((r) => (
                      <tr key={r._id}>
                        <td>{new Date(r.submittedAt).toLocaleString()}</td>
                        <td>{r.responseScope}</td>
                        <td>{r.membershipStatusAtSubmission}</td>
                        <td>
                          <ul className="answer-summary">
                            {r.answers.map((a) => (
                              <li key={a.fieldKey}>
                                <strong>{a.fieldKey}:</strong>{' '}
                                {Array.isArray(a.value) ? a.value.join(', ') : String(a.value ?? '')}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── ConsultationPage ─────────────────────────────────────────────────────────
// ─── OversightView ─────────────────────────────────────────────────────────────
// Shown to cip_admin / cip_staff. Read-only cross-tenant questionnaire summary.
const OversightView = () => {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getQuestionnaires({ includeArchived: 'true' })
      .then((r) => setQuestionnaires(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="page-loading">Loading…</p>;

  const grouped = questionnaires.reduce((acc, q) => {
    const key   = q.tenantId?._id || q.tenantId || 'unknown';
    const label = q.tenantId?.name || 'Unknown community';
    if (!acc[key]) acc[key] = { label, items: [] };
    acc[key].items.push(q);
    return acc;
  }, {});

  return (
    <div className="consultation-oversight">
      <div className="page-header">
        <h2>Platform Consultations — Oversight</h2>
        <p style={{ color: '#64748b' }}>Read-only view across all communities ({questionnaires.length} questionnaires)</p>
      </div>
      {Object.entries(grouped).map(([tid, { label, items }]) => (
        <div key={tid} style={{ marginBottom: 28 }}>
          <h4 style={{ color: '#94a3b8', textTransform: 'uppercase', fontSize: '0.78rem', letterSpacing: '0.06em', marginBottom: 8 }}>
            {label} — {items.length} questionnaire{items.length !== 1 ? 's' : ''}
          </h4>
          <table className="admin-table">
            <thead>
              <tr><th>Title</th><th>Status</th><th>Questions</th><th>Created</th></tr>
            </thead>
            <tbody>
              {items.map((q) => (
                <tr key={q._id}>
                  <td>{q.title}</td>
                  <td>
                    <span className={`status-badge status-${q.isActive ? 'active' : 'inactive'}`}>
                      {q.isActive ? 'Active' : 'Archived'}
                    </span>
                  </td>
                  <td>{q.fields?.length ?? 0}</td>
                  <td>{new Date(q.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

// ─── ConsultationPage ─────────────────────────────────────────────────────────
// mode prop:
//   'oversight'  → OversightView  (platform admin/staff, cross-tenant read-only)
//   'review'     → ManagerView    (community_admin, explicit override)
//   'contribute' → MemberView     (explicit override)
//   undefined    → derived from communityRole + panel
const ConsultationPage = ({ mode }) => {
  const location = useLocation();
  const { access, activeTenant, panel, user } = useAuth();
  const tenantCtx = useTenantSafe();
  const isEmbedded = new URLSearchParams(location.search).get('embedded') === '1';
  const wrapperProps = isEmbedded ? {} : { className: 'page consultation-page' };
  // Prefer TenantContext when inside /t/:slug/ (works for platform admins who have no activeTenant)
  const viewedTenant = tenantCtx ? tenantCtx.viewTenant : activeTenant;
  const tenantId = viewedTenant?._id;
  const viewerRole = tenantCtx?.viewerRole;
  const communityRole = viewerRole || (tenantId ? access?.communityRoles?.[tenantId] : null);

  // Derive effective mode if not explicitly passed
  const effectiveMode = mode || (() => {
    if (tenantCtx?.isPlatformStaff || panel === 'admin' || panel === 'staff') return 'oversight';
    if (communityRole === 'community_admin') return 'review';
    if (canStaffSection(user, tenantId, 'consultation')) return 'review';
    return 'contribute';
  })();

  if (tenantCtx?.loading) return <p className="page-loading">Loading community...</p>;
  if (tenantCtx?.error) return <p role="alert">{tenantCtx.error}</p>;

  if (effectiveMode === 'oversight') {
    return (
      <div {...wrapperProps}>
        {tenantId && <><h1>Consultation / Economic development</h1>
          <CommunityProjects key={tenantId} tenantId={tenantId} tenantSlug={viewedTenant?.slug} /></>}
        <OversightView />
      </div>
    );
  }

  if (!tenantId) {
    return (
      <div {...wrapperProps}>
        <p className="page-loading">No active community.</p>
      </div>
    );
  }

  return (
    <div {...wrapperProps}>
      <h1>Consultation / Economic development</h1>
      <CommunityProjects key={tenantId} tenantId={tenantId} tenantSlug={viewedTenant?.slug} />
      {effectiveMode === 'review'
        ? <ManagerView tenantId={tenantId} activeTenant={viewedTenant} />
        : <MemberView  tenantId={tenantId} activeTenant={viewedTenant} />
      }
    </div>
  );
};

export default ConsultationPage;
